import { useRagnar } from '@mentimeter/ragnar-react';
import type { PrioritisationResultT } from '@mentimeter/visualizations';
import { Prioritisation } from '@mentimeter/visualizations';
import {
  SvgElement,
  VisualizationUpdater,
  useUniqueSelector,
} from '@mentimeter/question-modules-shared';
import type { PrioritisationT } from '@mentimeter/question-modules-types';
import { ScreenReaderOnly } from '@mentimeter/ragnar-ui/screenreader-only';
import { memo, useMemo } from 'react';
import type { Question } from '@mentimeter/http-clients';
import { ScreenReaderTable } from './ScreenReaderTable';

interface Data extends PrioritisationResultT {
  index: number;
}

interface Props {
  selector: string;
  data: Data[];
  animationMultiplier: 1 | 0;
  lineColor: string;
  fillColors: string[];
  textColor: string;
  isScoreHidden: boolean;
}

const C = memo<Props>(
  ({
    selector,
    fillColors,
    textColor,
    lineColor,
    animationMultiplier,
    data,
    isScoreHidden,
  }: Props) => {
    const prioritisation = new Prioritisation({
      selector: `#${selector}`,
      data,
      animationMultiplier,
      lineColor,
      textColor,
      fillColors,
      isScoreHidden,
    });

    return (
      <SvgElement aria-hidden="true">
        <VisualizationUpdater updateFn={prioritisation.update} />
        <g id={selector} />
      </SvgElement>
    );
  },
);

interface PrioritisationVisualizationProps {
  question: Question;
  shouldAnimate: boolean;
  results: PrioritisationT['results'];
}

export const PrioritisationVisualization = ({
  question,
  shouldAnimate,
  results,
}: PrioritisationVisualizationProps) => {
  const {
    theme: {
      visualizationColors: { fillColors, textColor },
    },
  } = useRagnar();
  const animationMultiplier = shouldAnimate ? 1 : 0;
  const selector = useUniqueSelector();
  const { choices, hide_results } = question;

  const data = useMemo(() => {
    const hasScores = results.findIndex((d) => d.score) > -1;
    const resultsBeforeSorting = choices.map((choice, index) => {
      const result = results.find((result) => result.id === choice.id);
      const fallbackResult = {
        id: choice.id,
        label: choice.label,
        score: 0,
      };
      return { ...(result ?? fallbackResult), index }; // add index to keep same fillcolor mapping after sorting
    });

    if (!hasScores) return resultsBeforeSorting;
    return resultsBeforeSorting.sort((a, b) => b.score - a.score);
  }, [choices, results]);

  return (
    <>
      <C
        selector={selector}
        fillColors={fillColors}
        textColor={textColor}
        lineColor={textColor}
        animationMultiplier={animationMultiplier}
        data={data}
        isScoreHidden={hide_results}
      />
      <ScreenReaderOnly>
        <ScreenReaderTable results={data} shouldHideResults={hide_results} />
      </ScreenReaderOnly>
    </>
  );
};
